import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EditCommissionModal({ commission, policy, userInfo, open, onOpenChange, onSuccess }) {
  const [formData, setFormData] = useState({
    npn: '',
    commission_amount: '',
    paid_status: '',
    statement_date: '',
    comment: ''
  });
  const [npnVerified, setNpnVerified] = useState(null);
  const [npnVerifying, setNpnVerifying] = useState(false);
  const [verifiedAgentData, setVerifiedAgentData] = useState(null);
  const [originalNpn, setOriginalNpn] = useState('');
  const [originalAgentData, setOriginalAgentData] = useState(null);
  const [paidStatuses, setPaidStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (commission && open) {
      const existingNpn = commission.agent_on_commission_npn || commission.agentonCommission_npn || '';
      const agentData = {
        first_name: commission.agentonCommission_name?.split(' ')[0] || '',
        last_name: commission.agentonCommission_name?.split(' ').slice(1).join(' ') || '',
        status: 'active'
      };
      
      // Pre-fill form with existing commission data
      setFormData({
        npn: existingNpn,
        commission_amount: commission.commission_amount || '',
        paid_status: commission.paid_split || '',
        statement_date: commission.statement_date || '',
        comment: commission.comment || ''
      });
      
      // Store original NPN and agent data to track changes
      setOriginalNpn(existingNpn);
      setOriginalAgentData(agentData);
      
      // If NPN exists, mark as already verified (since it was verified when created)
      if (existingNpn) {
        setNpnVerified(true);
        setVerifiedAgentData(agentData);
      } else {
        setNpnVerified(null);
        setVerifiedAgentData(null);
      }
      loadPaidStatuses();
    }
  }, [commission, open]);

  const loadPaidStatuses = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/meta/commission/paidSplit');
      const statuses = Array.isArray(response.data) 
        ? response.data.map(s => ({ value: s.id, label: s.status }))
        : [];
      setPaidStatuses(statuses);
      setLoading(false);
    } catch (err) {
      console.error('Error loading paid statuses:', err);
      toast.error('Failed to load paid status options');
      setLoading(false);
    }
  };

  const verifyNPN = async () => {
    if (!formData.npn || formData.npn.length < 3) {
      toast.error('Please enter a valid NPN');
      return;
    }

    try {
      setNpnVerifying(true);
      const response = await axios.post(`${API}/auth/verify-npn`, { npn: formData.npn });
      
      const isValid = response.data?.found === true || 
                      response.data?.success === true || 
                      (response.data && Object.keys(response.data).length > 0 && !response.data.error);
      
      if (isValid) {
        setNpnVerified(true);
        setVerifiedAgentData(response.data);
        toast.success('NPN verified successfully');
      } else {
        setNpnVerified(false);
        setVerifiedAgentData(null);
        toast.error('NPN not found or invalid');
      }
    } catch (err) {
      console.error('Error verifying NPN:', err);
      setNpnVerified(false);
      setVerifiedAgentData(null);
      toast.error('Failed to verify NPN');
    } finally {
      setNpnVerifying(false);
    }
  };

  const handleNpnChange = (value) => {
    setFormData({ ...formData, npn: value });
    
    // If NPN changed from original, require re-verification
    if (value !== originalNpn) {
      setNpnVerified(null);
      setVerifiedAgentData(null);
    } else if (value === originalNpn && originalNpn) {
      // If changed back to original, restore verification
      setNpnVerified(true);
      setVerifiedAgentData(originalAgentData);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!npnVerified) {
      toast.error('Please verify the NPN first');
      return;
    }

    if (!formData.commission_amount) {
      toast.error('Please enter a commission amount');
      return;
    }

    if (!formData.paid_status) {
      toast.error('Please select a paid status');
      return;
    }

    setShowConfirmation(true);
  };

  const confirmSubmit = async () => {
    try {
      setSaving(true);
      
      const formDataObject = {
        agent_on_commission_npn: formData.npn || null,
        commission_amount: formData.commission_amount ? parseFloat(formData.commission_amount) : null,
        paid_split: formData.paid_status || null,
        statement_date: formData.statement_date || null,
        comment: formData.comment || null,
        submitted_by: userInfo?.user_id || null
      };

      const response = await axios.post(
        'https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/commission/update',
        {
          commission_id: commission.id,
          formData: formDataObject
        }
      );

      toast.success('Commission updated successfully');
      onSuccess && onSuccess();
      setShowConfirmation(false);
      onOpenChange(false);
    } catch (err) {
      console.error('Error updating commission:', err);
      toast.error('Failed to update commission');
    } finally {
      setSaving(false);
    }
  };

  if (!commission) return null;

  return (
    <>
      <Dialog open={open && !showConfirmation} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Commission</DialogTitle>
            <DialogDescription>
              Update commission record details
            </DialogDescription>
          </DialogHeader>

          {/* Commission Info Header */}
          <div className="bg-green-50 p-4 rounded-xl border border-green-200 space-y-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-neutral-500">Commission ID</p>
                <p className="text-xs font-mono text-neutral-600">{commission.id}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500">Policy Number</p>
                <p className="text-sm font-semibold text-neutral-800">{policy?.policy_number || commission.policy_number}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500">Policy Owner</p>
                <p className="text-sm font-semibold text-neutral-800">{policy?.agentOwner_name || commission.agentOwner_name}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500">Edited By</p>
                <p className="text-sm font-semibold text-neutral-800">
                  {userInfo?.first_name && userInfo?.last_name 
                    ? `${userInfo.first_name} ${userInfo.last_name}` 
                    : userInfo?.full_name || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#d4af37]" />
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                {/* Agent on Commission NPN */}
                <div className="space-y-2">
                  <Label htmlFor="npn">
                    Agent on Commission NPN <span className="text-red-500">*</span>
                    {originalNpn && (
                      <span className="text-xs text-neutral-500 font-normal ml-2">
                        (Leave as-is or change to verify new agent)
                      </span>
                    )}
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="npn"
                      value={formData.npn}
                      onChange={(e) => handleNpnChange(e.target.value)}
                      placeholder="Enter NPN"
                      required
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      onClick={verifyNPN}
                      disabled={npnVerifying || !formData.npn}
                      variant="outline"
                      className="min-w-[100px]"
                    >
                      {npnVerifying ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verifying
                        </>
                      ) : npnVerified === true ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                          Verified
                        </>
                      ) : npnVerified === false ? (
                        <>
                          <XCircle className="w-4 h-4 mr-2 text-red-600" />
                          Invalid
                        </>
                      ) : (
                        'Verify'
                      )}
                    </Button>
                  </div>
                  {npnVerified === false && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      NPN not found. Please verify the NPN is correct.
                    </p>
                  )}
                  {npnVerified === true && verifiedAgentData && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm font-semibold text-green-800">
                        ✓ Agent Verified
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        {verifiedAgentData.first_name} {verifiedAgentData.last_name}
                      </p>
                      {verifiedAgentData.status && (
                        <p className="text-xs text-green-600 mt-1">
                          Status: {verifiedAgentData.status}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Commission Amount */}
                <div className="space-y-2">
                  <Label htmlFor="amount">
                    Commission Amount <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={formData.commission_amount}
                    onChange={(e) => setFormData({ ...formData, commission_amount: e.target.value })}
                    placeholder="Enter amount (can be negative)"
                    required
                  />
                  <p className="text-xs text-neutral-500">
                    Accepts decimal and negative numbers (e.g., -34.16 for chargebacks)
                  </p>
                </div>

                {/* Paid Status */}
                <div className="space-y-2">
                  <Label htmlFor="paid_status">
                    Paid Status <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.paid_status}
                    onValueChange={(value) => setFormData({ ...formData, paid_status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select paid status" />
                    </SelectTrigger>
                    <SelectContent>
                      {paidStatuses.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Statement Date */}
                <div className="space-y-2">
                  <Label htmlFor="statement_date">Statement Date</Label>
                  <Input
                    id="statement_date"
                    type="date"
                    value={formData.statement_date}
                    onChange={(e) => setFormData({ ...formData, statement_date: e.target.value })}
                  />
                </div>

                {/* Comment */}
                <div className="space-y-2">
                  <Label htmlFor="comment">Comment (Optional)</Label>
                  <Textarea
                    id="comment"
                    value={formData.comment}
                    onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                    placeholder="Add any notes or comments"
                    rows={3}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!npnVerified}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Update Commission
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Commission Update</DialogTitle>
            <DialogDescription>
              Please review the updated commission details
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Policy:</span>
              <span className="text-sm font-semibold">{policy?.policy_number || commission.policy_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Agent NPN:</span>
              <span className="text-sm font-semibold">{formData.npn}</span>
            </div>
            {verifiedAgentData && (
              <div className="flex justify-between">
                <span className="text-sm text-neutral-600">Agent Name:</span>
                <span className="text-sm font-semibold text-green-700">
                  {verifiedAgentData.first_name} {verifiedAgentData.last_name}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Amount:</span>
              <span className={`text-sm font-bold ${parseFloat(formData.commission_amount) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                ${parseFloat(formData.commission_amount).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Paid Status:</span>
              <span className="text-sm font-semibold">
                {paidStatuses.find(s => s.value === formData.paid_status)?.label}
              </span>
            </div>
            {formData.comment && (
              <div>
                <span className="text-sm text-neutral-600">Comment:</span>
                <p className="text-sm mt-1 text-neutral-800">{formData.comment}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfirmation(false)}
              disabled={saving}
            >
              Back
            </Button>
            <Button
              onClick={confirmSubmit}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Confirm & Update'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
